import React, { useState, useRef, useEffect } from "react";

interface StoryProps {
  images: string[];
}

const InstaStory: React.FC<StoryProps> = ({ images }) => {
  const [selectedImage, setSelectedImage] = useState<string>(
    `${process.env.PUBLIC_URL}/girl1.jpg`
  ); // 기본 큰 사진 경로

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [startScrollLeft, setStartScrollLeft] = useState(0);

  // 작은 사진 클릭 시, 해당 이미지가 스크롤 중앙에 오도록 스크롤
  const scrollToCenter = (index: number) => {
    if (scrollContainerRef.current) {
      const imageElement = scrollContainerRef.current.children[
        index
      ] as HTMLElement;
      const containerWidth = scrollContainerRef.current.clientWidth;

      // 이미지의 위치와 크기 계산
      const imageOffsetLeft = imageElement.offsetLeft;
      const imageWidth = imageElement.clientWidth;

      // 스크롤 위치 계산 (이미지가 중앙에 오도록)
      const scrollPosition =
        imageOffsetLeft - (containerWidth - imageWidth) / 2;

      // 스크롤 위치 업데이트
      scrollContainerRef.current.style.scrollBehavior = "smooth";
      scrollContainerRef.current.scrollLeft = scrollPosition;
    }
  };

  // 마우스 드래그 시작
  const onMouseDown = (e: React.MouseEvent) => {
    if (scrollContainerRef.current) {
      setIsDragging(true);
      setStartX(e.clientX);
      setStartScrollLeft(scrollContainerRef.current.scrollLeft);
      scrollContainerRef.current.style.transition = "none"; // 애니메이션 비활성화
    }
  };

  // 마우스 이동
  const onMouseMove = (e: MouseEvent) => {
    if (isDragging && scrollContainerRef.current) {
      const deltaX = e.clientX - startX;
      scrollContainerRef.current.scrollLeft = startScrollLeft - deltaX;
    }
  };

  // 마우스 드래그 끝
  const onMouseUp = () => {
    setIsDragging(false);
    if (scrollContainerRef.current) {
      scrollContainerRef.current.style.transition = "scroll-left 0.2s ease"; // 애니메이션 활성화
    }
  };

  // 터치 드래그 시작 (모바일에서 스와이프 가능)
  const onTouchStart = (e: React.TouchEvent) => {
    if (scrollContainerRef.current) {
      setIsDragging(true);
      setStartX(e.touches[0].clientX);
      setStartScrollLeft(scrollContainerRef.current.scrollLeft);
      scrollContainerRef.current.style.transition = "none"; // 애니메이션 비활성화
    }
  };

  // 터치 이동
  const onTouchMove = (e: TouchEvent) => {
    if (isDragging && scrollContainerRef.current) {
      const deltaX = e.touches[0].clientX - startX;
      scrollContainerRef.current.scrollLeft = startScrollLeft - deltaX;
    }
  };

  // 터치 드래그 끝
  const onTouchEnd = () => {
    setIsDragging(false);
    if (scrollContainerRef.current) {
      scrollContainerRef.current.style.transition = "scroll-left 0.2s ease"; // 애니메이션 활성화
    }
  };

  // 마우스와 터치 이벤트 바인딩
  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
      document.addEventListener("touchmove", onTouchMove, { passive: false });
      document.addEventListener("touchend", onTouchEnd);
    } else {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      document.removeEventListener("touchmove", onTouchMove);
      document.removeEventListener("touchend", onTouchEnd);
    }
    return () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      document.removeEventListener("touchmove", onTouchMove);
      document.removeEventListener("touchend", onTouchEnd);
    };
  }, [isDragging]);

  return (
    <div className="flex flex-col items-center relative">
      <div className="relative w-full max-w-full">
        {/* 작은 사진만 스크롤 가능한 영역 */}
        <div
          ref={scrollContainerRef}
          className="flex space-x-4 overflow-x-scroll p-4 cursor-grab"
          style={{
            overflowX: "auto",
            pointerEvents: "auto",
            scrollbarWidth: "none",
          }}
          onMouseDown={onMouseDown} // 드래그 시작
          onTouchStart={onTouchStart} // 터치 시작
        >
          {images.map((image, index) => (
            <div
              key={index}
              className="flex-shrink-0 w-24 h-24 rounded-full p-[2px] bg-gradient-to-r from-[#f9ce34] via-[#ee2a7b] to-[#6228d7]"
              onClick={() => {
                setSelectedImage(image);
                scrollToCenter(index);
              }}
            >
              {/* 내부 이미지 */}
              <div className="w-full h-full rounded-full overflow-hidden bg-white p-[2px]">
                <img
                  src={image}
                  alt={`Thumbnail ${index + 1}`}
                  className="w-full h-full object-cover rounded-full"
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 여기서 다음 뷰는 스크롤이 되지 않도록 설정 */}
      <div
        className="w-full p-0"
        style={{
          pointerEvents: "none", // 이 영역은 클릭과 스크롤을 막는다.
          overflow: "hidden", // 스크롤을 막는다.
        }}
      ></div>
    </div>
  );
};

export default InstaStory;
